<!--
 * @Author: Tahiti
 * @Date: 2025-12-03 17:54:09
 * @LastEditors: Tahiti
 * @LastEditTime: 2026-01-06 22:35:12
 * @Description: 
 * @FilePath: /weryai-web/components/ui/CreatedOrder.vue
-->
<template>
    <Transition name="slide-in-right">
        <div
            v-if="visible && countdown > 0"
            class="fixed bottom-[80px] md:bottom-4 right-6 z-[50] cursor-pointer transition-all duration-300"
        >
            <img
                class="absolute top-[-55px] left-[10px] z-[-1] w-[67px] h-[89px]"
                src="https://static.weryai.com/growth/image/ca246bda1db4bcb9e135f7dbcdf714b3.webp"
                alt="purchase"
            />
            <img
                class="absolute top-[-80px] right-0 w-[200px] z-[-1]"
                src="https://static.weryai.com/growth/image/e703427ae6ff3ee6fe493cefecd859e7.webp"
                alt="purchase-bg"
            />
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                class="absolute top-[-22px] right-[-18px] z-[1] cursor-pointer hover:rotate-180 transition-all duration-300"
                @click="handleCloseForever"
            >
                <rect
                    width="24"
                    height="24"
                    rx="12"
                    fill="white"
                    fill-opacity="0.2"
                />
                <path
                    d="M8.1748 8.17285C8.40946 7.93861 8.78987 7.93843 9.02441 8.17285L11.999 11.1475L14.9727 8.1748C15.2073 7.9403 15.5877 7.94018 15.8223 8.1748C16.0567 8.40943 16.0566 8.78977 15.8223 9.02441L12.8477 11.998L15.8223 14.9727C16.0569 15.2073 16.0569 15.5876 15.8223 15.8223C15.5876 16.0568 15.2073 16.0569 14.9727 15.8223L11.998 12.8477L9.02344 15.8232C8.78877 16.0579 8.40849 16.0579 8.17383 15.8232C7.93938 15.5885 7.93924 15.2083 8.17383 14.9736L11.1484 11.998L8.1748 9.02344C7.94014 8.78875 7.94014 8.40754 8.1748 8.17285Z"
                    fill="white"
                />
            </svg>
            <div class="order-content" @click="handleShowPendingOrder">
                <span class="flex items-center justify-start">
                    <span class="order-text">{{
                        t('order_modal.pending_order_content')
                    }}</span>
                    <span v-if="is_yearly" class="percentage">
                        {{
                            yearDiscountEnums[
                                recentCreatedOrder?.goods?.subscription_type
                            ]
                        }}</span
                    >
                </span>
                <!-- 倒计时 -->
                <div v-if="countdown > 0" class="countdown-container">
                    <span class="countdown-box">{{ formatTime(minutes) }}</span>
                    <span class="countdown-separator">:</span>
                    <span class="countdown-box">{{ formatTime(seconds) }}</span>
                </div>
            </div>
        </div>
    </Transition>
</template>
<script setup lang="ts">
import { useAppStore } from '~/stores/app'
import { computed, ref, watch, onUnmounted, nextTick } from 'vue'
import { emitter } from '~/utils/emitter'
import { useI18n } from '~/composables/useI18n'
import { useToolsHooks } from '~/utils/useHooks'
import { sendCreatedOrderApi } from '~/api/billing'

const appStore = useAppStore()
const { yearDiscountEnums } = useToolsHooks()
const recentCreatedOrder = computed(() => appStore.recentCreatedOrder)
const is_yearly = computed(
    () => recentCreatedOrder.value?.goods?.period_type === 'YEARLY'
)
const { t } = useI18n()
const visible = ref(false)
const shouldAnimate = ref(false) // 控制是否应该显示动画
const isClosing = ref(false) // 控制是否正在关闭（滑出动画）
const countdown = ref(0) // 总毫秒数
let countdownTimer: NodeJS.Timeout | null = null
let startTime = 0 // 开始时间戳

// 格式化时间，确保两位数显示
const formatTime = (time: number): string => {
    return time.toString().padStart(2, '0')
}

// 计算分钟、秒、毫秒（百分之一秒，00-99）
const minutes = computed(() => Math.floor(countdown.value / 60000))
const seconds = computed(() => Math.floor((countdown.value % 60000) / 1000))
const milliseconds = computed(() => Math.floor((countdown.value % 1000) / 10))

// 启动倒计时
const startCountdown = () => {
    if (countdownTimer) {
        clearInterval(countdownTimer)
    }

    if (recentCreatedOrder.value?.left_seconds) {
        // 将秒数转换为毫秒
        countdown.value = recentCreatedOrder.value.left_seconds * 1000
        startTime = Date.now()

        countdownTimer = setInterval(() => {
            const elapsed = Date.now() - startTime
            const remaining =
                recentCreatedOrder.value!.left_seconds * 1000 - elapsed

            if (remaining > 0) {
                countdown.value = remaining
            } else {
                countdown.value = 0
                if (countdownTimer) {
                    clearInterval(countdownTimer)
                    countdownTimer = null
                }
            }
        }, 100) // 每100ms更新一次，以显示毫秒
    }
}

const handleClose = async () => {
    // 先触发滑出动画
    isClosing.value = true
    shouldAnimate.value = false

    // 等待动画完成后再隐藏元素和清理数据
    setTimeout(() => {
        appStore.recentCreatedOrder = null
        visible.value = false
        isClosing.value = false
        if (countdownTimer) {
            clearInterval(countdownTimer)
            countdownTimer = null
        }
    }, 300) // 动画持续时间 300ms
}

const handleShowPendingOrder = () => {
    emitter.emit('show-pending-order')
    handleClose()
}

// 关闭弹窗并取消待支付订单提醒
const handleCloseForever = () => {
    sendCreatedOrderApi(
        recentCreatedOrder.value?.goods?.id,
        'click_cancel_pay_pending'
    )
    handleClose()
}

watch(
    () => recentCreatedOrder.value,
    newVal => {
        if (newVal) {
            // 先重置动画状态，确保每次都能重新触发动画
            shouldAnimate.value = false
            visible.value = true
            // 使用 nextTick 确保 DOM 更新后再触发动画
            nextTick(() => {
                // 添加小延迟确保 DOM 完全渲染
                setTimeout(() => {
                    shouldAnimate.value = true
                }, 10)
            })
            startCountdown()
        } else {
            shouldAnimate.value = false
            isClosing.value = false
            countdown.value = 0
            if (countdownTimer) {
                clearInterval(countdownTimer)
                countdownTimer = null
            }
        }
    },
    {
        immediate: true,
    }
)

onUnmounted(() => {
    if (countdownTimer) {
        clearInterval(countdownTimer)
        countdownTimer = null
    }
})
</script>

<style scoped lang="less">
.order-content {
    width: 183px;
    min-height: 90px;
    padding: 10px;
    border-radius: 10px;
    background: linear-gradient(130deg, #fff 20.84%, #fffad2 76.71%);
}

.order-text {
    font-size: 20px;
    font-weight: 600;
    background: linear-gradient(90deg, #ffdc6b 0%, #ff62b0 100%);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
}
.percentage {
    border-radius: 4px;
    background: linear-gradient(270deg, #ff5eb7 0%, #ff8d5c 100%);
    color: #fff;
    text-align: center;
    font-size: 12px;
    font-weight: 400;
    line-height: normal;
    padding: 2px 4px;
    margin-left: 3px;
}

.countdown-container {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 10px;
    gap: 4px;
}

.countdown-box {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    background: #fff;
    border-radius: 10px;
    color: #000;
    font-size: 16px;
    font-weight: 600;
    line-height: 1;
}

.countdown-separator {
    color: #000;
    font-size: 16px;
    font-weight: 600;
    margin: 0 2px;
}

// 从右边滑入的动画效果
.slide-in-container {
    transform-origin: right center;
    // 初始状态：在屏幕右侧外面
    transform: translateX(100%);
    opacity: 0;
}

// 当 shouldAnimate 为 true 时，触发滑入动画
.slide-in-container.animate-slide-in {
    animation: slideInRight 0.5s ease-out forwards;
}

// 当 isClosing 为 true 时，触发滑出动画
.slide-in-container.animate-slide-out {
    animation: slideOutRight 0.3s ease-in forwards;
}

@keyframes slideInRight {
    from {
        transform: translateX(100%);
        opacity: 0;
    }
    to {
        transform: translateX(0);
        opacity: 1;
    }
}

@keyframes slideOutRight {
    from {
        transform: translateX(0);
        opacity: 1;
    }
    to {
        transform: translateX(100%);
        opacity: 0;
    }
}

// Vue Transition 进入动画（作为备用）
.slide-in-right-enter-from {
    transform: translateX(100%);
    opacity: 0;
}

.slide-in-right-enter-active {
    transition:
        transform 0.5s ease-out,
        opacity 0.5s ease-out;
}

.slide-in-right-enter-to {
    transform: translateX(0);
    opacity: 1;
}

// Vue Transition 离开动画
.slide-in-right-leave-from {
    transform: translateX(0);
    opacity: 1;
}

.slide-in-right-leave-active {
    transition:
        transform 0.3s ease-in,
        opacity 0.3s ease-in;
}

.slide-in-right-leave-to {
    transform: translateX(100%);
    opacity: 0;
}
</style>
